import React, { useState, useRef, useEffect } from 'react';

export interface OptimizedImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'loading'> {
  src: string;
  alt: string;
  quality?: number;
  format?: 'webp' | 'jpeg' | 'png';
  priority?: boolean;
  loading?: 'lazy' | 'eager';
  cropToSquare?: boolean;
  squareSize?: number;
  preloadOnHover?: boolean;
  onPreload?: () => void;
}

// Cache of preloaded images
const preloadedImages = new Set<string>();

// Preload an image
export function preloadImage(url: string): Promise<void> {
  if (preloadedImages.has(url)) return Promise.resolve();

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      preloadedImages.add(url);
      resolve();
    };
    img.onerror = () => resolve();
    img.src = url;
  });
}

// Preload multiple images
export function preloadImages(urls: string[]): void {
  urls.forEach(url => preloadImage(url));
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  quality = 90,
  format = 'webp',
  priority = false,
  cropToSquare = false,
  squareSize = 400,
  preloadOnHover = false,
  onPreload,
  onMouseEnter,
  ...imgProps
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Check if the URL is from Salesforce and needs proxying
  const needsProxy = src && (src.includes('salesforce.com') || src.includes('force.com'));

  // Generate optimized image URL
  const getOptimizedSrc = () => {
    if (!src) return '';

    // If cropToSquare is requested, always use proxy for processing
    if (cropToSquare || needsProxy) {
      const params = new URLSearchParams({
        url: src,
        format,
        quality: quality.toString(),
      });

      // Add square cropping parameters if requested
      if (cropToSquare) {
        params.append('width', squareSize.toString());
        params.append('height', squareSize.toString());
        params.append('fit', 'cover');
      }

      return `/api/images/proxy?${params.toString()}`;
    }

    return src;
  };

  const optimizedSrc = getOptimizedSrc();

  // Handle hover preloading
  const handleMouseEnter = (e: React.MouseEvent<HTMLImageElement>) => {
    if (preloadOnHover && onPreload) {
      onPreload();
    }
    onMouseEnter?.(e);
  };

  // Check if already in browser cache
  useEffect(() => {
    if (imgRef.current?.complete) {
      setIsLoaded(true);
    }
  }, []);

  return (
    <img
      ref={imgRef}
      {...imgProps}
      src={optimizedSrc}
      loading={priority ? 'eager' : imgProps.loading || 'lazy'}
      onMouseEnter={handleMouseEnter}
      onLoad={(e) => {
        setIsLoaded(true);
        imgProps.onLoad?.(e);
      }}
      style={{
        ...imgProps.style,
        opacity: isLoaded ? 1 : 0,
        transition: 'opacity 0.2s ease-in-out, transform 0.5s ease-in-out',
      }}
    />
  );
};

export default OptimizedImage;
